import type { CSSProperties, ReactNode } from 'react'
import { Skeleton } from '../../../core/index.js'
import type { FacilityHeatmapView, HeatState } from '../../types.js'
import type { TodayFilter } from '../today/todayData.js'
import { HeatLegend } from './HeatLegend.js'
import { filteredDay, gridOf, heatPaint, HEAT_SWATCH, hoursOfDay, nowOffsetPct, pctOf, ratioState, SHIFT_LABEL, slotNote, slotTime, type HeatPaint, type HeatTime } from './heatmapData.js'

const paintStyle = (paint: HeatPaint): CSSProperties => ({ background: paint.background, color: paint.color, border: paint.border })

function Swatch({ state, className = 'h-3.5 w-3.5' }: { state: HeatState; className?: string }) {
  return <span aria-hidden="true" className={`inline-block shrink-0 rounded-sm ${className}`} style={{ background: HEAT_SWATCH[state].fill }} />
}

export interface DayHeatmapProps {
  view: FacilityHeatmapView | null
  /** The day to draw: today, or another day of the view's month chosen in the week strip; a day outside the month draws nothing. */
  date: string
  /** `HH:mm` where the facility is, so a shift of today under way is told from one ahead; a day ahead is striped throughout, a past day solid. */
  now: string
  filter: TodayFilter
  unitName: (unitId: string) => string
  /** What sits beside the grid: on Today, the open shifts in flight (design screen 13, 27 October 2026). */
  aside?: ReactNode
}

/**
 * The day's state as a heatmap (26 October 2026, the user's design): the day's verdict on the facility's scale, the
 * day hour by hour with a marker at now, every unit and role by shift type coloured by its state - solid once worked
 * or under way, striped while still ahead - and what made the day its colour, worst first. The unit and role filter
 * narrows it, judged by the API's scale. Since 27 October 2026 it is Today's one coverage view: the week strip under
 * Census chooses the day it draws, a day ahead with every cell striped and no hour strip, a past day solid throughout,
 * and the column beside the grid is the caller's (Today puts the open shifts in flight there) - the list of what made
 * the day its colour is gone, since each cell already says what is short and where it stands (the user's call).
 */
export function DayHeatmap({ view, date, now, filter, unitName, aside }: DayHeatmapProps) {
  if (!view) {
    return <Skeleton lines={5} label="Loading the day’s state" />
  }
  if (!view.hasStaffingPlan) {
    return <p className="text-xs text-ink-mid">The day is judged against the staffing plan; set one under Configuration › Scheduling › Census.</p>
  }
  if (!view.released) {
    return <p className="text-xs text-ink-mid">This month is not released, so nobody holds a seat yet. Build and release it on the Month tab and the day fills in here.</p>
  }
  const day = view.days.find((entry) => entry.date === date)
  if (!day) {
    return null
  }
  const { state, tally, slots } = filteredDay(day, filter, view.scale)
  const when: 'past' | 'today' | 'ahead' = date < view.today ? 'past' : date > view.today ? 'ahead' : 'today'
  if (tally.required === 0) {
    return <p className="text-xs text-ink-mid">Nothing is required {filter.unitId || filter.role ? 'for this unit or role ' : ''}{when === 'today' ? 'today' : 'that day'}: no census is recorded or forecast to require seats.</p>
  }
  // A past day was worked whatever its shifts' phases; a day ahead is ahead throughout; today is read against now.
  const timeOf = (slot: Parameters<typeof slotTime>[0]): HeatTime => (when === 'ahead' ? 'ahead' : when === 'past' ? 'worked' : slotTime(slot, now))
  const hours = when === 'today' ? hoursOfDay(slots, now, view.scale) : []
  const marker = nowOffsetPct(hours, now)
  const grid = gridOf(slots)
  const verdict = heatPaint(state, 'worked')
  const chips = [
    { state: 'critical' as const, count: tally.ranShort, text: 'run short' },
    { state: 'short' as const, count: tally.uncovered, text: 'not on the board' },
    { state: 'near' as const, count: tally.onBoard, text: 'on the board now' },
    { state: 'covered' as const, count: tally.staffed, text: 'held' },
  ].filter((chip) => chip.count > 0)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-stretch gap-4">
        <div className="flex min-h-[8.5rem] w-48 shrink-0 flex-col justify-between gap-2 rounded-lg px-4 py-3" style={paintStyle(verdict)}>
          <span className="text-[10px] font-bold uppercase tracking-wide">{when === 'past' ? 'The day was' : when === 'ahead' ? 'The day will be' : 'The day is'}</span>
          <span className="text-3xl font-black leading-none">{HEAT_SWATCH[state].label}</span>
          <span className="text-xs font-semibold">
            {tally.staffed} of {tally.required} seats held · {pctOf(tally.staffed, tally.required)}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 basis-80 flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            {chips.map((chip) => (
              <span key={chip.text} className="inline-flex items-center gap-1.5 rounded-full border border-marine/15 px-2.5 py-1 text-[11px] font-semibold text-ink">
                <Swatch state={chip.state} />
                {chip.count} {chip.text}
              </span>
            ))}
          </div>
          {when === 'ahead' && <p className="text-[11px] text-ink-mid">A day ahead: every cell is striped, and where the census is a forecast the requirement can still move once it is recorded.</p>}
          {when === 'past' && <p className="text-[11px] text-ink-mid">A worked day: what its board held when it was worked.</p>}
          {hours.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">Hour by hour · seats on duty against the plan</span>
              <div className="relative pt-5">
                <div className="grid grid-cols-[repeat(24,minmax(0,1fr))] gap-[2px]" role="img" aria-label={`The day hour by hour from ${hours[0]?.label}`}>
                  {hours.map((hour) => (
                    <span key={hour.label} className="h-8 rounded-sm" style={paintStyle(heatPaint(hour.state, hour.time === 'live' ? 'worked' : hour.time))} title={`${hour.label} · ${hour.staffed} of ${hour.required} seats · ${HEAT_SWATCH[hour.state].label}`} />
                  ))}
                </div>
                {marker !== null && (
                  <>
                    <span aria-hidden="true" className="absolute bottom-[-4px] top-4 w-0.5 bg-ink" style={{ left: `${marker}%` }} />
                    <span aria-hidden="true" className="absolute top-0 -translate-x-1/2 rounded bg-ink px-1.5 text-[10px] font-bold text-white" style={{ left: `${marker}%` }}>
                      Now {now}
                    </span>
                  </>
                )}
              </div>
              <div className="grid grid-cols-6 text-[10px] text-ink-mid">
                {hours.filter((_, index) => index % 4 === 0).map((hour) => (
                  <span key={hour.label}>{hour.label}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={`grid items-start gap-4 ${aside ? "lg:grid-cols-[3fr_2fr]" : ""}`}>
        <div className="overflow-x-auto">
          <div className="grid min-w-[28rem] gap-1 text-[11px]" style={{ gridTemplateColumns: `7rem repeat(${grid.shiftTypes.length}, minmax(0, 1fr)) 4.5rem` }}>
            <span />
            {grid.shiftTypes.map((shiftType) => (
              <span key={shiftType} className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">
                {SHIFT_LABEL[shiftType]}
              </span>
            ))}
            <span className="text-center text-[10px] font-bold uppercase tracking-wide text-ink-mid">Row</span>
            {grid.rows.map((row) => {
              const rowState = row.tally.required > 0 ? heatPaint(ratioState(row.tally.staffed, row.tally.required, view.scale), 'worked') : null
              return (
                <div key={`${row.unitId}-${row.role}`} className="contents">
                  <span className="flex flex-col justify-center py-1 font-bold text-ink">
                    {row.role}
                    <span className="text-[10px] font-normal text-ink-mid">{unitName(row.unitId)}</span>
                  </span>
                  {row.cells.map((slot, index) =>
                    slot ? (
                      <div key={slot.shiftType} className="flex min-h-[3.5rem] flex-col justify-between rounded-md px-2 py-1.5" style={paintStyle(heatPaint(slot.state, timeOf(slot) === 'ahead' ? 'ahead' : 'worked'))} title={`${unitName(slot.unitId)} ${slot.role} ${SHIFT_LABEL[slot.shiftType]}: ${HEAT_SWATCH[slot.state].label}`}>
                        <span className="flex items-baseline justify-between gap-1">
                          <span>
                            <span className="text-base font-black">{slot.staffed}</span>
                            <span className="font-semibold"> / {slot.required}</span>
                          </span>
                          {timeOf(slot) === 'live' && <span className="h-2 w-2 animate-pulse rounded-full bg-current" aria-label="Under way" />}
                        </span>
                        <span className="text-[10px] font-semibold leading-tight">{slotNote(slot)}</span>
                      </div>
                    ) : (
                      <span key={grid.shiftTypes[index]} className="rounded-md bg-canvas" />
                    ),
                  )}
                  <span className="flex flex-col items-center justify-center gap-0.5 rounded-md bg-canvas">
                    {rowState && <span aria-hidden="true" className="h-4 w-4 rounded" style={{ background: rowState.background }} />}
                    <span className="font-bold text-ink">
                      {row.tally.staffed}/{row.tally.required}
                    </span>
                  </span>
                </div>
              )
            })}
          </div>
        </div>
        {aside && <div className="flex min-w-0 flex-col gap-2 lg:border-l lg:border-marine/15 lg:pl-4">{aside}</div>}
      </div>
      <HeatLegend />
    </div>
  )
}

